import { useCallback, useMemo, useState } from "react";

import type {
  FilterConfig,
  FilterMode,
  FilterState,
  FilterValue,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { buildWhereFromState, countActiveFilters } from "#app/lib/filters/build-where";

const EMPTY_STATE: FilterState = { blocks: [], logic: "AND" };

/**
 * Generic filter state hook.
 *
 * Simple and advanced modes maintain SEPARATE FilterState objects.
 * The `where` clause and `activeFilterCount` are derived from whichever
 * mode is currently active.
 *
 * `initial` seeds the starting mode/state (mount-only, like a `useState`
 * initializer) — for surfaces that open straight into the advanced builder,
 * e.g. a widget's transient header filter popover.
 */
export function useFilters(
  config: FilterConfig,
  initial?: { mode?: FilterMode; advancedState?: FilterState },
): UseFiltersResult {
  const [activeMode, setActiveMode] = useState<FilterMode>(initial?.mode ?? "simple");
  const [simpleState, setSimpleState] = useState<FilterState>(EMPTY_STATE);
  const [advancedState, setAdvancedState] = useState<FilterState>(
    initial?.advancedState ?? EMPTY_STATE,
  );

  // ---------------------------------------------------------------------------
  // Quick filter values — a view onto simpleState.blocks[0]
  // ---------------------------------------------------------------------------

  const quickValues = useMemo(() => {
    if (!config.quickFilters || simpleState.blocks.length === 0) return {};
    const block0 = simpleState.blocks[0];
    const values: Record<string, unknown> = {};
    for (const qf of config.quickFilters) {
      const filter = block0?.filters.find((f) => f.field === qf.field);
      values[qf.field] = filter?.value ?? null;
    }
    return values;
  }, [simpleState, config.quickFilters]);

  const setQuickValue = useCallback(
    (field: string, value: unknown) => {
      const qf = config.quickFilters?.find((q) => q.field === field);
      if (!qf) return;

      setSimpleState((prev) => {
        // Simple mode keeps every quick filter in ONE block, so the edit below
        // always has a first block to land in: the existing one, copied so the
        // splice/push is not a mutation of the previous state, or a fresh one.
        const first = prev.blocks[0];
        const block: FilterState["blocks"][number] = first
          ? { ...first, filters: [...first.filters] }
          : { logic: "AND", filters: [] };
        const blocks = [block, ...prev.blocks.slice(1)];
        const idx = block.filters.findIndex((f) => f.field === field);

        const isEmptyValue =
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0) ||
          value === "";

        if (isEmptyValue) {
          if (idx >= 0) block.filters.splice(idx, 1);
        } else if (idx >= 0) {
          block.filters[idx] = { field, operator: qf.operator, value };
        } else {
          block.filters.push({ field, operator: qf.operator, value });
        }

        // Remove empty blocks
        const nextBlocks = blocks.filter((b) => b.filters.length > 0);
        return { ...prev, blocks: nextBlocks };
      });
    },
    [config.quickFilters]
  );

  // ---------------------------------------------------------------------------
  // Advanced filter mutations (operate on advancedState)
  // ---------------------------------------------------------------------------

  const addBlock = useCallback(() => {
    setAdvancedState((prev) => ({
      ...prev,
      blocks: [
        ...prev.blocks,
        { logic: "AND", filters: [{ field: "", operator: "eq", value: null }] },
      ],
    }));
  }, []);

  const removeBlock = useCallback((index: number) => {
    setAdvancedState((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((_, i) => i !== index),
    }));
  }, []);

  const setBlockLogic = useCallback(
    (index: number, logic: "AND" | "OR") => {
      setAdvancedState((prev) => ({
        ...prev,
        blocks: prev.blocks.map((b, i) =>
          i === index ? { ...b, logic } : b
        ),
      }));
    },
    []
  );

  const setMainLogic = useCallback((logic: "AND" | "OR") => {
    setAdvancedState((prev) => ({ ...prev, logic }));
  }, []);

  const addFilter = useCallback((blockIndex: number) => {
    setAdvancedState((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b, i) =>
        i === blockIndex
          ? {
              ...b,
              filters: [
                ...b.filters,
                { field: "", operator: "eq", value: null },
              ],
            }
          : b
      ),
    }));
  }, []);

  const updateFilter = useCallback(
    (
      blockIndex: number,
      filterIndex: number,
      update: Partial<FilterValue>
    ) => {
      setAdvancedState((prev) => ({
        ...prev,
        blocks: prev.blocks.map((b, bi) =>
          bi === blockIndex
            ? {
                ...b,
                filters: b.filters.map((f, fi) =>
                  fi === filterIndex ? { ...f, ...update } : f
                ),
              }
            : b
        ),
      }));
    },
    []
  );

  const removeFilter = useCallback(
    (blockIndex: number, filterIndex: number) => {
      setAdvancedState((prev) => {
        const nextBlocks = prev.blocks
          .map((b, bi) =>
            bi === blockIndex
              ? { ...b, filters: b.filters.filter((_, fi) => fi !== filterIndex) }
              : b
          )
          .filter((b) => b.filters.length > 0);

        return { ...prev, blocks: nextBlocks };
      });
    },
    []
  );

  const clearAll = useCallback(() => {
    if (activeMode === "simple") {
      setSimpleState(EMPTY_STATE);
    } else {
      setAdvancedState(EMPTY_STATE);
    }
  }, [activeMode]);

  // ---------------------------------------------------------------------------
  // Derived values — from whichever mode is active
  // ---------------------------------------------------------------------------

  const activeState = activeMode === "simple" ? simpleState : advancedState;

  const activeFilterCount = useMemo(
    () => countActiveFilters(activeState),
    [activeState]
  );

  const where = useMemo(
    () => buildWhereFromState(activeState),
    [activeState]
  );

  return useMemo(() => ({
    activeMode,
    setActiveMode,
    filterState: simpleState,
    advancedState,
    quickValues,
    setQuickValue,
    addBlock,
    removeBlock,
    setBlockLogic,
    setMainLogic,
    addFilter,
    updateFilter,
    removeFilter,
    clearAll,
    activeFilterCount,
    where,
  }), [
    activeMode, setActiveMode, simpleState, advancedState, quickValues,
    setQuickValue, addBlock, removeBlock, setBlockLogic, setMainLogic,
    addFilter, updateFilter, removeFilter, clearAll, activeFilterCount, where,
  ]);
}
